import { useAtomValue } from "jotai";
import { Dispatch, SetStateAction, useMemo } from "react";
import { Button } from "reactstrap";
import { findWhere } from "underscore";

import useGlobals from "../../../api/api-hooks/globals/useGlobal";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import AppFieldCard from "../../../elements/AppFieldCard";
import { AppConfirm } from "../../../utils/PopupUtil";
import { Layout, LayoutType, Prefill } from "../../../types/GlobalInterfaces";
import { GlobalsTypes } from "../../../views/cloud/Globals/Globals";
import { SharedViewModes } from "../../PageView/PageViewRenderer";
import { useNavigate } from "react-router-dom";

export const useGoToGlobalPage = (type: LayoutType = "Quick Create") => {
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom);

  const appConfirmData = {
    title: `Do you want to go to the Global ${type} Manager panel?`,
    icon: "question",
    confirmButtonColor: "var(--primary)",
    text: `Since this is a Global ${type}, you have to modify it from the ${type} Manager Panel.`,
    confirmButtonText: "Yes",
  };

  const value = {
    "Quick Create": "QuickCreates",
    Layout: "Layouts",
  };

  const goToGlobalPage = () =>
    AppConfirm(appConfirmData).then((data) => {
      if (data.isConfirmed) {
        navigate(`/clouds/${tenantID}/settings/globals/${value[type]}`);
      }
    });

  return { goToGlobalPage };
};

type SelectedQCS = { [key: string]: any };

interface Props<T extends Layout | Prefill> {
  selectedGlobals: SelectedQCS;
  setSelectedGlobals: Dispatch<SetStateAction<SelectedQCS>>;
  existingValues: any[];
  moduleAPIName: string | undefined;
  globalType: GlobalsTypes;
  layoutType?: SharedViewModes;
}

function CreateFromGlobalPage<T extends Layout | Prefill>({
  selectedGlobals,
  setSelectedGlobals,
  existingValues,
  moduleAPIName,
  globalType,
  layoutType,
}: Props<T>) {
  const { globals, globalsLoading } = useGlobals<any[]>(globalType);
  const { goToGlobalPage } = useGoToGlobalPage(
    globalType === "QuickCreates" ? "Quick Create" : "Layout"
  );
  const formatGlobalType = globalType.split(/(?=[A-Z])/).join(" ");

  const selectableGlobals = useMemo(() => {
    if (globalsLoading) return [];
    if (!Array.isArray(globals) || globals?.length === 0) return [];

    const layoutT = layoutType;

    return globals.filter((global) => {
      if (global.ModuleAPIName !== moduleAPIName) return false;

      if (layoutT && (global as Layout)?.Type !== layoutT) return false;

      return !findWhere(existingValues, { GlobalID: global.id });
    });
  }, [existingValues, globals, globalsLoading]);

  if (globalsLoading) return null;

  return (
    <>
      <div className="pb-2 mb-3 border-bottom d-flex align-items-center justify-content-between">
        <div>
          <h3 className="mb-0">Cloud {formatGlobalType}</h3>
          <small>Select existing {formatGlobalType} to add to this view</small>
        </div>
        <Button className="btn-icon-only" outline onClick={goToGlobalPage}>
          <i className="mdi mdi-pencil" />
        </Button>
      </div>
      {selectableGlobals.length > 0 ? (
        selectableGlobals.map((qc: any) => {
          return (
            // @ts-ignore
            <AppFieldCard
              key={qc?.id}
              onClick={() => {
                setSelectedGlobals((prev) => {
                  if (prev[qc?.id]) {
                    delete prev[qc?.id];
                  } else {
                    prev[qc?.id] = true;
                  }
                  return { ...prev };
                });
              }}
              isSelected={selectedGlobals[qc?.id]}
              icon={"mdi mdi-earth"}
              name={qc?.Name}
              full
              css="mb-2"
              innerCSS="mx-1"
            />
          );
        })
      ) : (
        <div className="small px-4 py-2 border rounded bg-ultralight">
          <span className="text-dark font-weight-600">
            No {formatGlobalType} to add.
          </span>
          <br />
          You&apos;ve either already added them all
          <br />
          Or there are none associated with this data source.
        </div>
      )}
    </>
  );
}

export default CreateFromGlobalPage;
